import { useContext, useMemo } from 'react'
import {
  buildPanelCheckItems,
} from 'components/Cruises/SearchPage/Filters/Inputs/CruiseExtraFiltersInputs'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'

import { trackEvent } from 'analytics/analytics'
import { mapCruiseLineFilterToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { CRUISE_TYPE_FILTER_FACETS } from 'constants/cruise'
import useCruiseTypeFilterLabel from 'hooks/Cruise/useCruiseTypeFilterLabel'

type CruiseTypeFacet = {
  count?: number
  label?: string
  slug: string
}

interface Props {
  cruiseTypes?: Set<string>
  onChange: (value: any) => void
  cruiseTypeFacets: Array<App.CruiseSearchFacetV2Item>
}

function CruiseCruiseTypeFilter({
  cruiseTypes,
  onChange,
  cruiseTypeFacets,
}: Props) {
  const formatCruiseTypeLabel = useCruiseTypeFilterLabel()
  const globalSearchState = useContext(GlobalSearchStateContext)

  const { cruiseTypeItems, hasCruiseTypes } = useMemo(() => {
    const cruiseTypeFacetSlugs = new Set(cruiseTypeFacets.map(facet => facet.slug.toLowerCase()))
    const cruiseTypeFilterFacets = CRUISE_TYPE_FILTER_FACETS.filter(
      facet => !cruiseTypeFacetSlugs.has(facet.slug.toLowerCase()),
    )
    const combinedCruiseTypeFacets: Array<CruiseTypeFacet> = [
      ...cruiseTypeFacets,
      ...cruiseTypeFilterFacets.map(facet => ({ slug: facet.slug, label: facet.slug })),
    ]

    return {
      cruiseTypeItems: buildPanelCheckItems(combinedCruiseTypeFacets.map(({ count: _count, label, slug, ...facet }) => ({
        ...facet,
        slug,
        label: formatCruiseTypeLabel(label ?? slug) ?? (label ?? slug),
      }))),
      hasCruiseTypes: combinedCruiseTypeFacets.some(facet => facet.count == null || facet.count > 0),
    }
  }, [cruiseTypeFacets, formatCruiseTypeLabel])

  if (!hasCruiseTypes) {
    return null
  }

  return <div data-testid="cruise-type-filter">
    <FilterPanelCheckboxGroup
      name="cruiseTypes"
      items={cruiseTypeItems}
      onChange={(e) => {
        trackEvent(searchEventWithContext({
          searchEvent: { type: 'filters' },
          sortContext: { value: 'recommended' },
          searchContext: mapCruiseLineFilterToSnowplowSearchEvent(globalSearchState),
          filterContexts: [{
            label: e.target.value,
            type: 'cruise_type',
            value: e.target.checked ? 'selected' : 'unselected',
          }],
        }))

        onChange(e)
      }}
      defaultSelected={cruiseTypes}
    />
  </div>
}

export default CruiseCruiseTypeFilter
